import { addDays, type DateKey, isDateKey, type ScheduleStart } from '@arrow/game-engine';

import type { KeyValueStore } from './kv.ts';

/**
 * Where this environment's schedule starts, as last seen from the api: `{ date, number }`,
 * the archive's `earliest` (No. 1), or worked out from any daily the api served (a date
 * and its number, one number a day). Kept so the screens that don't fetch the archive
 * (Today, Stats) can still leave out local records from days the schedule no longer has,
 * e.g. the test environment's 1–24 October, deleted when 25 October became No. 1
 * (docs/DEPLOYMENT.md, "The numbering reset"), and so a past day's number needs no fetch.
 *
 * The start only ever moves later: a schedule loses days at its front, it never grows them
 * back, and a stale cached daily (an old number) must not undo what the api now says.
 * Every read is defensive; unknown or malformed, it's null and nothing is left out.
 */
export const SCHEDULE_START_KEY = 'arrow:v1:schedule-start';

/** The date of No. 1 that `start` implies. */
const origin = (start: ScheduleStart): DateKey => addDays(start.date, -(start.number - 1));

function valid(value: unknown): value is ScheduleStart {
	if (typeof value !== 'object' || value === null) return false;
	const { date, number } = value as Record<string, unknown>;
	return isDateKey(date) && typeof number === 'number' && Number.isInteger(number) && number >= 1 && number < 100_000;
}

export function readScheduleStart(store: KeyValueStore): ScheduleStart | null {
	try {
		const raw = store.get(SCHEDULE_START_KEY);
		if (raw === null) return null;
		const value: unknown = JSON.parse(raw);
		return valid(value) ? { date: value.date, number: value.number } : null;
	} catch {
		return null;
	}
}

/** Remember `start` if it's later than what's kept (or nothing is). Never throws. */
export function noteScheduleStart(start: unknown, store: KeyValueStore): void {
	try {
		if (!valid(start)) return;
		const kept = readScheduleStart(store);
		if (kept) {
			const [was, now] = [origin(kept), origin(start)];
			if (now < was || (now === was && start.date <= kept.date)) return;
		}
		store.set(SCHEDULE_START_KEY, JSON.stringify({ date: start.date, number: start.number }));
	} catch {
		// Not remembered: the next daily or archive response tries again.
	}
}

/** A daily the api served: its date and number say where No. 1 is. Never throws. */
export function noteDaily(date: unknown, number: unknown, store: KeyValueStore): void {
	try {
		if (!isDateKey(date) || typeof number !== 'number' || !Number.isInteger(number) || number < 1) return;
		noteScheduleStart({ date: addDays(date, -(number - 1)), number: 1 }, store);
	} catch {
		// As above.
	}
}
